// src\app\modules\analyseNodes.js

import { IN_YEAR, JARGON, POSITION, POSITION_ENUM } from '../modules/constants'
import { getNameProp, yearsElapsed } from "../modules/utilities"
import HOOK from "../components/hooks"

const analyseNodes = {

  setLinks: props => {
    const { nodes, showNonOverlapping } = props

    let links = []

    if (nodes.length > 0) {
      nodes.forEach(node => analyseNode({ ...props, links, node }))
      // must do this first to create links array, before...

      // if not showing non-overlapping nodes, filter overlapping nodes & links out
      if (!showNonOverlapping) {
        filterOverlappingNodes(nodes, links);
      }
    }

    // Sort by tense so that links are rendered highest (e.g. past) tense first,
    // so that current is rendered on top of past
    links.sort((a, b) => a.tense < b.tense ? 1 : a.tense > b.tense ? -1 : 0)

    // Sort so that PSC ones are first, and are therefore rendered under officer links
    // ("officer" < "psc")
    links.sort((a, b) => a.capacity === b.capacity ? 0 : a.capacity < b.capacity ? 1 : -1)
    // console.log("links: ", links)
    return links;
  }
}

const analyseNode = props => {
  // console.log('analyseNode props:',props)
  const { connections, hubId, node } = props
  // console.log("node: ", node)

  const nodeId = node.entity.entityId;
  node.has = {}
  node.roles = []

  setFlagsToFalse2(node.has)
  const connectionCounts = initconnectionCounts()

  for (let connectionKey in node.entity.connectionIds) {
    let connection = connections[node.entity.connectionIds[connectionKey]]
    // console.log('connection:', connection)
    connection.start = new Date("01/01/2100");
    connection.end = null;
    setFlagsToFalse2(connection);
    connection.hasConnection = !!Object.keys(connection.capacities).length
    connection.connections.any.any = !!Object.keys(connection.capacities).length
    iterateCapacities({ ...props, connection, connectionCounts, hubId, nodeId })

    // console.log("connection: ", connection);
    // console.log('node.connectionCounts:',node.connectionCounts)
    incrementCounts3({ connection, connectionCounts });
  } // for (let connectionKey in node.entity.connectionIds) {

  // debugCount(nodeId, connectionCounts);

  node.connectionCounts = connectionCounts // add connectionCounts to hub node
  // console.log('node:', node)
}

const iterateCapacities = props => {

  let { connection, hubId, node, nodeId, nodes } = props
  // for each capacity
  for (let capacity in connection.capacities) {
    const connectionHasStakeholder = connection.parentId === nodeId  // if false, indicates an appointment
    iterateRoles({ ...props, connectionHasStakeholder, capacity })
    setNodeRole(node)
    updateNodeFlags2(node.has, connection)
    // console.log('node.has:', node.has)
    updateNodeDates(hubId, node, nodes, connection)
  }
}

const iterateRoles = props => {
  let { checkboxes, connection, connectionHasStakeholder, hubId, capacity, links, node, nodeId, nodes, enableSecondLevel } = props

  for (var role of connection.capacities[capacity]) {
    if (node.level === 1 && (connection.parentId === hubId || connection.childId === hubId)) {
      if (!!(checkboxes[HOOK.TENSE] & role.tenseBit)) {
        node.roles.push(role.roleDescription)
      }
    }
    else if (node.level === 2) {
      const viaNode = nodes.find(n => n.id === node.lConnectId)
      if (viaNode?.entity) {
        node.roles.push(role.roleDescription)
        node.via = {
          indexBit: viaNode.entity.companyProps?.lse?.indexBit,
          name: viaNode.entity.name,
          role: role.roleDescription,
        }
      }
    }

    setConnectionStart(connection, role)
    setConnectionEnd(connection, role)

    if (bothNodesExist({ connection, nodeId, nodes })
      && !!(checkboxes[HOOK.TENSE] & role.tenseBit)
      && !!(checkboxes[HOOK.POSITION] & role.position.typeBit)) {
      // console.log("nodeId: " + nodeId)
      const otherNodeId = nodeId === connection.childId
        ? connection.parentId
        : connection.childId
      const otherNode = nodes.find(node => node.id === otherNodeId)
      // console.log("otherNodeId: " + otherNodeId)

      createLink({ connection, capacity, links, role, node, otherNode, enableSecondLevel })
    }
    iterateRoles2({ role, connection, connectionHasStakeholder, capacity })
  }
  // console.log("nodes: ", nodes)
}

const iterateRoles2 = ({ role, connection, connectionHasStakeholder, capacity }) => {
  const tense = ["current", "notice", "past", "future"][role.tense]
  connection.connections.any[tense] = true
  // Update tenseBits to reflect all appointment tenses
  connection.connections.tenseBits = (connection.connections.tenseBits | role.tenseBit)

  const set = (obj, capacity, tense) => {
    // console.log('obj:',obj)
    obj.any.any = true
    obj.any[tense] = true
    obj[capacity].any = true
    obj[capacity][tense] = true
  }

  if (connectionHasStakeholder) {
    set(connection.connections, "stakeholder", tense)
    set(connection.connections, capacity, tense)
  }
  else {
    set(connection.appointments, capacity, tense)
  }
}

const createLink = ({ connection, capacity, links, role, node, otherNode, enableSecondLevel }) => {
  let thisNode = {}
  let level = 0
  let viaName = null
  let interconnection = false

  if (enableSecondLevel) {
    if (node.level === 2) {
      thisNode = node
      viaName = getNameProp.toPresent(otherNode.entity.name)
      level = 2
    } else if (otherNode.level === 2) {
      thisNode = otherNode
      viaName = getNameProp.toPresent(node.entity.name)
      level = 2
    } else {
      thisNode = node.level === 1 ? node : otherNode
      level = 1
    }
  } else { // not showing secondlevel
    thisNode = node.level === 1 ? node : otherNode
    level = 1

    // detect and flag interconnections
    if (node.level === 1 && otherNode.level === 1) {
      interconnection = true
      // console.log("level 1 interconnection!")
    }
  }
  // if (node.level === 2 && otherNode.level === 2)
  //   console.log("level 2 interconnection!")

  links.push({ //  NB link corresponds to a 'role'
    capacity,
    compensation: role.compensationGBP,
    connection, // only needed due to nexusConnectionInfo - TODO change that file to use data in link
    end: role.until ? new Date(role.until) : null,
    interconnection,
    lseCompany: !!thisNode.entity.companyProps?.lse,
    isOfficer: connection.childId === thisNode.id,
    level,
    position: role.position.title,
    positionType: role.position.type,
    role: role.roleType,
    roleDescription: role.roleDescription,
    roleDuration: role.days / IN_YEAR.DAYS,
    source: connection.childId,
    start: role.from ? new Date(role.from) : null,
    target: connection.parentId,
    tense: role.tense,
    thisNode,
    value: 1,
    viaName,
  })
}

const setConnectionStart = (connection, role) => {
  const startDate = new Date(role.from)
  // if(!role.from)
  //   console.log("role.from: " + role.from)
  if (connection.start !== undefined && startDate < connection.start)
    connection.start = startDate
}

const setConnectionEnd = (connection, role) => {
  let endDate = new Date(role.until)
  if (connection.end !== undefined && endDate > connection.end)
    connection.end = endDate

  if (!role.until)
    connection.end = undefined
  // console.log("connection.end: ", connection.end)
}

const bothNodesExist = ({ connection, nodeId, nodes }) => {
  // Check that both nodes exist
  // TODO: Check this is correct - use nodes.every()?
  let otherNodeId = nodeId === connection.childId
    ? connection.parentId
    : connection.child

  let bothNodesExist = false;

  nodes.forEach(anyNode => {
    if (anyNode.entity.entityId === otherNodeId)
      bothNodesExist = true
  })

  return bothNodesExist
}

const setNodeRole = node => {
  const rolesString = node.level === 2
    ? node.via?.role : node.roles ? node.roles.join(", ") : ""

  // console.log(node)
  // if( node.entity.position )
  //   console.log(node.id + " - " + node.entity.position.name)
  node.role = node.entity.position && node.entity.position.name &&
    node.entity.position.name !== POSITION[POSITION_ENUM.OTHER].NAME
    ? `${node.entity.position.name}`
    : rolesString === JARGON.NO_ROLE
      ? "Director"
      : `${rolesString}`
}

const incrementCounts3 = ({ connection, connectionCounts }) => {

  const increment = (obj, counts) => {
    // console.log('obj:',obj)
    if (obj.any) counts.total++
    if (obj.current) counts.current++
    if (obj.notice) counts.notice++
    if (obj.past) counts.past++
    if (obj.future) counts.future++
  }

  // connections
  increment(connection.connections.any, connectionCounts.connections.total)

  // stakeholders
  increment(connection.connections.stakeholder, connectionCounts.connections.stakeholder)
  increment(connection.connections.officer, connectionCounts.connections.officer)
  increment(connection.connections.psc, connectionCounts.connections.psc)

  // appointments
  increment(connection.appointments.any, connectionCounts.appointments.total)
  increment(connection.appointments.officer, connectionCounts.appointments.officer)
  increment(connection.appointments.psc, connectionCounts.appointments.psc)
}

const updateNodeFlags2 = (nodeHas, connection) => {

  const set = (obj1, obj2) => {
    if (obj1.any) obj2.any = true
    if (obj1.current) obj2.current = true
    if (obj1.notice) obj2.notice = true
    if (obj1.past) obj2.past = true
    if (obj1.future) obj2.future = true
  }

  set(connection.connections.any, nodeHas.connections.any)
  set(connection.connections.stakeholder, nodeHas.connections.any)
  set(connection.connections.stakeholder, nodeHas.connections.officer)
  set(connection.connections.stakeholder, nodeHas.connections.psc)
  set(connection.connections.stakeholder, nodeHas.connections.stakeholder)

  set(connection.appointments.any, nodeHas.appointments.any)
  set(connection.appointments.officer, nodeHas.appointments.officer)
  set(connection.appointments.psc, nodeHas.appointments.psc)
}

const updateNodeDates = (hubId, node, nodes, connection) => {

  if (node.level === 1 &&     // find connection to hub node and set start and end
    (connection.parentId === hubId || connection.childId === hubId)) {
    node.connectionStart = connection.start
    node.connectionEnd = connection.end
    node.connectionOverlapStart = node.connectionStart
    node.connectionOverlapEnd = node.connectionEnd ? node.connectionEnd : null // new Date()

    updateDuration(node)
    updateOverlap(node)

  } else if (node.level === 2) {
    // find connections to level 1 nodes
    let otherId = null
    if (connection.parentId === node.id) otherId = connection.childId
    else if (connection.childId === node.id) otherId = connection.parentId
    else return // should never get here

    const otherNode = nodes.find(node => node.id === otherId)

    if (otherNode && otherNode.level === 1) {     // other node is a level 1 node
      // console.log("id: " + node.id)
      // set connection dates
      node.connectionStart = connection.start   // set node start to earliest connection start

      if (!connection.end) // no end, so connection must be current
        node.connectionEnd = null // null means conection is current
      else if (node.connectionEnd === undefined // not yet set
        || (node.connectionEnd !== null && connection.end > node.connectionEnd))  // connection start is before existing node start
        node.connectionEnd = connection.end   // set node start to connection start

      // set overlap dates
      if (!node.connectionOverlapStart || node.connectionStart < node.connectionOverlapStart)
        node.connectionOverlapStart = node.connectionStart // set to earlier of multiple connection starts

      // if other (level 1) node start is later, then set overlap start to that
      if (otherNode.connectionStart > node.connectionOverlapStart)
        node.connectionOverlapStart = otherNode.connectionStart

      if (!node.connectionOverlapEnd)
        node.connectionOverlapEnd = otherNode.connectionEnd
          ? otherNode.connectionEnd
          : new Date()

      if (node.connectionEnd && node.connectionEnd < node.connectionOverlapEnd) {
        // console.log("set overlap end")
        node.connectionOverlapEnd = node.connectionEnd
      }

      // console.log("overlap start: ", node.connectionOverlapStart + ", end: ", node.connectionOverlapEnd)

      updateDuration(node)
      updateOverlap(node)
    }
  }
}

const updateDuration = node => {
  if (node.connectionStart) {
    const today = new Date()
    const startMonths = (12 * node.connectionStart.getFullYear()) + node.connectionStart.getMonth()

    const endMonths = node.connectionEnd
      ? (12 * node.connectionEnd.getFullYear()) + node.connectionEnd.getMonth()
      : (12 * today.getFullYear()) + today.getMonth()

    // duration for this node may have already been set for a previous connection
    const duration = (endMonths - startMonths) / 12

    if (!node.connectionDuration || duration > node.connectionDuration)
      node.connectionDuration = duration
  }
}

// const updateOverlap = node => {
//   // console.log("node: " + node.id + ", overlap start: ", node.connectionOverlapStart)
//   // console.log("node: " + node.id + ", overlap end: ", node.connectionOverlapEnd)
//   if (node.connectionOverlapStart) {
//     const duration = yearsElapsed(node.connectionOverlapStart, node.connectionOverlapEnd)

//     if ((!node.connectionOverlapDuration || duration > node.connectionOverlapDuration)) { // && startMonths !== 1080) {
//       node.connectionOverlapDuration = duration
//     }
//   }
// }

const updateOverlap = node => {
  // console.log("node: " + node.id + ", overlap start: ", node.connectionOverlapStart)
  // console.log("node: " + node.id + ", overlap end: ", node.connectionOverlapEnd)

  if (node.connectionOverlapStart) {
    const today = new Date()
    const startMonths = (12 * node.connectionOverlapStart.getFullYear()) + node.connectionOverlapStart.getMonth()
    // console.log("startMonths=" + startMonths)

    const endMonths = node.connectionOverlapEnd
      ? (12 * node.connectionOverlapEnd.getFullYear()) + node.connectionOverlapEnd.getMonth() + 1
      : (12 * today.getFullYear()) + today.getMonth() + 1
    // console.log("endMonths=" + endMonths)

    // duration for this node may have already been set for a previous connection
    const duration = endMonths - startMonths > 0 ? (endMonths - startMonths) / 12 : 0
    if ((!node.connectionOverlapDuration || duration > node.connectionOverlapDuration) &&
      startMonths !== 1080) {
      node.connectionOverlapDuration = duration

      // set the via node
    }
  }
}

const filterOverlappingNodes = (nodes, links) => {
  // loop through all nodes to find ones to remove
  nodes.forEach(node => {
    if ((node.level === 1 && node.connectionOverlapDuration < 0) || (node.level === 2 && node.connectionOverlapDuration <= 0)) {
      // find index of node in nodes array
      node.remove = true
    }
  })

  // remove those nodes and their links
  let lookForNodeToRemove = true
  while (lookForNodeToRemove) {
    const nodeIndex = nodes.findIndex(node => node.remove)

    // remove non overlapping node from nodes array
    if (nodeIndex >= 0) { // node found
      const nodeId = nodes[nodeIndex].id

      nodes.splice(nodeIndex, 1)

      // remove its links too
      let lookForThisNodesLinks = true
      while (lookForThisNodesLinks) { // more overlapping links left
        const linkIndex = links.findIndex(link =>
          link.source === nodeId ||
          link.target === nodeId)

        if (linkIndex >= 0) // link found
          links.splice(linkIndex, 1)
        else
          lookForThisNodesLinks = false
      }

    } else
      lookForNodeToRemove = false
  }
}

const setFlagsToFalse2 = has => {

  const init = () => {
    return {
      current: false,
      notice: false,
      past: false,
      future: false,
      any: false,
    }
  }

  has.connections = {
    officer: init(),
    psc: init(),
    stakeholder: init(),
    any: init(),
    tenseBits: 0,
  }

  has.appointments = {
    officer: init(),
    psc: init(),
    any: init(),
  }
}

const initconnectionCounts = () => {
  const init = () => {
    return {
      current: 0,
      notice: 0,
      past: 0,
      future: 0,
      total: 0,
    }
  }

  return {
    connections: {
      officer: init(),
      psc: init(),
      stakeholder: init(),
      total: init(),
    },
    appointments: {
      officer: init(),
      psc: init(),
      total: init(),
    },
  }
}

export default analyseNodes
